import React, { useCallback, useEffect, useState } from 'react';
import { Monitor, Users, Network, RefreshCw, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from '../../lib/api';
import Sparkline from './components/Sparkline';

const PAGE_SIZE = 20;

export default function UsageReports() {
  const api = useApi();
  const [totals, setTotals]       = useState(null);
  const [perTenant, setPerTenant] = useState([]);
  const [error, setError]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([api.adminUsageTotals(), api.adminUsagePerTenant()]);
      setTotals(t);
      setPerTenant(p.rows || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="admin-banner error">{error}</div>;
  if (!totals) return <div style={{ color: '#9ca3af', padding: 32 }}>Loading…</div>;

  const series = (totals.series || []).map(p => ({ label: p.bucket.slice(5), value: p.total_users }));
  const overCount = perTenant.filter(r => r.max_users != null && r.active_users > r.max_users).length;
  const totalPages = Math.max(1, Math.ceil(perTenant.length / PAGE_SIZE));
  const paged = perTenant.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageNums = buildPageNums(page, totalPages);

  return (
    <>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Usage</h1>
          <p className="admin-page-subtitle">Active users across tenants and growth trends.</p>
        </div>
        <div className="admin-header-actions">
          <button className="btn-secondary" onClick={load} disabled={loading} style={{ gap: 6 }}>
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="admin-kpi-row">
        <div className="admin-kpi">
          <div className="admin-kpi-icon blue"><Monitor size={18} /></div>
          <p className="admin-kpi-label">Total apps</p>
          <p className="admin-kpi-value">{totals.totalApps}</p>
          <p className="admin-kpi-sub">Tenant workspaces</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon green"><Users size={18} /></div>
          <p className="admin-kpi-label">Total signed-in users</p>
          <p className="admin-kpi-value">{totals.totalUsers}</p>
          <p className="admin-kpi-sub">Across all apps</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon purple"><Network size={18} /></div>
          <p className="admin-kpi-label">Total memberships</p>
          <p className="admin-kpi-value">{totals.totalMemberships}</p>
          <p className="admin-kpi-sub">User × app pairings</p>
        </div>
        <div className={`admin-kpi ${overCount > 0 ? 'warn' : ''}`}>
          <div className={`admin-kpi-icon ${overCount > 0 ? 'orange' : 'purple'}`}><AlertTriangle size={18} /></div>
          <p className="admin-kpi-label">Over limit</p>
          <p className="admin-kpi-value">{overCount}</p>
          <p className="admin-kpi-sub">Exceeding user quota</p>
        </div>
      </div>

      {/* Growth chart */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Active users over time</h2>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Snapshots written every 6h by usage reconciliation job</span>
        </div>
        {series.length === 0 ? (
          <div className="admin-empty">No snapshot data yet.</div>
        ) : (
          <Sparkline data={series} />
        )}
      </div>

      {/* Per-tenant table */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Per-tenant usage ({perTenant.length})</h2>
        </div>
        {perTenant.length === 0 ? (
          <div className="admin-empty">No data.</div>
        ) : (
          <div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>APP</th>
                <th>PLAN</th>
                <th>ACTIVE USERS</th>
                <th>LIMIT</th>
                <th>UTILISATION</th>
                <th>LAST SNAPSHOT</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(r => {
                const over = r.max_users != null && r.active_users > r.max_users;
                const pct  = r.max_users ? Math.round((r.active_users / r.max_users) * 100) : null;
                return (
                  <tr key={r.app_id}>
                    <td style={{ fontWeight: 600, color: '#0f1c2e' }}>{r.app_name}</td>
                    <td style={{ color: r.plan_code ? '#374151' : '#d1d5db' }}>{r.plan_code || '—'}</td>
                    <td style={{ color: over ? '#d97706' : '#374151', fontWeight: over ? 700 : 400 }}>
                      {r.active_users}
                      {over && <span style={{ marginLeft: 6, fontSize: 11, color: '#d97706' }}>over limit</span>}
                    </td>
                    <td>{r.max_users == null ? '∞' : r.max_users}</td>
                    <td>
                      {pct != null ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: over ? '#ef4444' : '#3b82f6', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, color: '#6b7280', minWidth: 32 }}>{pct}%</span>
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {r.last_snapshot_at ? r.last_snapshot_at.slice(0, 16).replace('T', ' ') : 'never'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, perTenant.length)} to {Math.min(page * PAGE_SIZE, perTenant.length)} of {perTenant.length} tenants
            </span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtn(page === 1)}>
                <ChevronLeft size={14} />
              </button>
              {pageNums.map((p, i) =>
                p === '…'
                  ? <span key={`e-${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                  : <button key={p} onClick={() => setPage(p)} style={pageBtn(false, page === p)}>{p}</button>
              )}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtn(page === totalPages)}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </>
  );
}

function buildPageNums(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1, 2, 3];
  if (cur > 3 && cur < total - 1) { pages.push('…', cur); }
  pages.push('…', total);
  return [...new Set(pages)];
}

function pageBtn(disabled, active) {
  return {
    width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6,
    background: active ? '#0f1c2e' : 'white',
    color: disabled ? '#d1d5db' : active ? 'white' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
